import { useLocation, useOutlet } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MotionConfig, AnimatePresence, motion } from 'motion/react';
import NavHeader from './NavHeader';
import AnnouncementBanner from './AnnouncementBanner';
import Footer from './Footer';
import CustomCursor from '../ui/CustomCursor';
import WhatsAppButton from '../ui/WhatsAppButton';
import TrustBadgeEmbed from '../ui/TrustBadgeEmbed';
import { BookingModalProvider } from '../booking/BookingModalContext';
import { LeaveSiteConfirmProvider } from '../ui/LeaveSiteConfirm';
import { BootstrapProvider } from '../../lib/BootstrapContext';
import { usePageSeoContext } from '../../lib/PageSeoContext';
import { getSiteOrigin } from '../../lib/siteUrl';
import { schedulePageViewTracking } from '../../lib/pageViewTracking';

function LodgingBusinessJsonLd() {
  const { siteSettings } = usePageSeoContext();
  const origin = getSiteOrigin();
  const siteName = siteSettings.seo_site_name || 'Homeland Stay';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: siteName,
    url: origin,
    telephone: siteSettings.contact_phone || undefined,
    address: siteSettings.contact_address
      ? {
          '@type': 'PostalAddress',
          streetAddress: siteSettings.contact_address,
          addressLocality: 'Ahmedabad',
          addressRegion: 'Gujarat',
          addressCountry: 'IN',
        }
      : undefined,
    sameAs: [siteSettings.social_instagram, siteSettings.social_facebook, siteSettings.social_youtube].filter(Boolean),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export default function Layout() {
  const location = useLocation();
  // Captured once per render, not re-read by the exiting instance — the
  // exiting motion.div keeps whatever element this was at the moment its
  // key stopped matching, which is what lets it animate out showing the
  // *old* page rather than snapping to the new route immediately (a plain
  // <Outlet /> would do the latter, since it always renders the current
  // match regardless of which AnimatePresence child asked for it).
  const element = useOutlet();

  useEffect(() => {
    window.scrollTo(0, 0);
    return schedulePageViewTracking(location.pathname);
  }, [location.pathname]);

  return (
    <MotionConfig reducedMotion="user">
      <BootstrapProvider>
        <BookingModalProvider>
          <LeaveSiteConfirmProvider>
            <div className="font-sans bg-paper min-h-screen flex flex-col">
              <LodgingBusinessJsonLd />
              <CustomCursor />
              <div className="sticky top-0 z-50">
                <AnnouncementBanner />
                <NavHeader />
              </div>
              <main className="flex-1 relative">
                {/* No `mode="popLayout"` -- this AnimatePresence only ever has one
                    child (a full-page opacity crossfade on route change), so
                    there are no siblings for popLayout's layout-projection
                    machinery to reflow around. That machinery still runs its own
                    synchronous DOM geometry measurement on every render even
                    when unused, which showed up as a measurable forced reflow
                    on page load. */}
                <AnimatePresence initial={false}>
                  <motion.div
                    key={location.pathname}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, ease: 'easeInOut' }}
                  >
                    {element}
                  </motion.div>
                </AnimatePresence>
              </main>
              <Footer />
              <WhatsAppButton />
              <TrustBadgeEmbed />
            </div>
          </LeaveSiteConfirmProvider>
        </BookingModalProvider>
      </BootstrapProvider>
    </MotionConfig>
  );
}
